/**
 * Typed client for the per-user mission-service sidecar at `/api/mission/*`.
 *
 * Wire chain (handoff Part D + lexruntime-mission-architecture §7):
 *   SPA (this file)  → vite dev proxy / SPA origin in prod
 *   lexserver        → `lexserver/src/routes/runtime-mission-proxy.ts`
 *                      strips `/api/mission` → `/mission/...`
 *   plugin proxy     → `lexruntime/openclaw/extensions/lexiun-core/
 *                       src/mission-service/proxy.ts` (PREFIX `/mission`)
 *                      strips `/mission` → forwards remainder to
 *   mission-service  → loopback :3220, routes in
 *                      `lexruntime/mission-service/src/router.ts`
 *
 * Two URL shapes the SPA produces (the doubled-prefix is REAL because the
 * plugin proxy strips ONE `/mission` segment but mission-service itself
 * namespaces its COMMAND routes under `/mission/*`):
 *   - COLLECTION routes (singular strip):
 *       GET    /api/mission/missions
 *       GET    /api/mission/missions/:id
 *       GET    /api/mission/missions/:id/result
 *       GET    /api/mission/missions/:id/tasks
 *       DELETE /api/mission/missions/:id
 *       GET    /api/mission/health
 *   - COMMAND routes (doubled `/mission/`):
 *       POST   /api/mission/mission/open
 *       POST   /api/mission/mission/:id/resume
 *
 * WS `/ws/missions/:id` is deliberately NOT exported — the plugin proxy
 * returns 501 for WebSocket upgrades and the architecture pins browser
 * monitoring to POLLING (architecture §7 + §14). The Missions pane uses
 * `useMissionsPoll` (5s active / 30s idle + Page Visibility) instead.
 *
 * Auth + envelope rules:
 *   - `Authorization: Bearer <Lexiun JWT>` per call, sourced from
 *     `getToken()` (no client-side cache; matches `lx-openclaw-config.ts`).
 *   - W3C `traceparent` header per call so the mission-service logs
 *     co-locate with the SPA event that triggered them.
 *   - Mission-service returns RAW JSON (no `{ok:true, data:...}`
 *     envelope wrap); DO NOT unwrap `body.data`.
 *   - Error shape: either `{error:"<code>"}` (bare string), or
 *     `{error:"<code>", message:"…"}`, or `{error:{code, message}}`.
 *     `request()` normalises both and surfaces `code` on `MissionApiError`.
 *
 * Canonical contract: `lexruntime/mission-service/src/contract.ts`
 * (Zod schemas — kept here as plain TS interfaces; server-side validation
 * is authoritative, matches the cousin clients' no-Zod stance).
 */

import { getToken } from "@/lib/lexiun-jwt";
import { newTraceparent } from "@/lib/traceparent";

// ─── Wire types (mirror lexruntime/mission-service/src/contract.ts) ───

/** Closed enum of mission states — gating plan §A.2 + contract.ts:27. */
export type MissionState =
  | "queued"
  | "submitted"
  | "planning"
  | "running"
  | "awaiting_user"
  | "synthesizing"
  | "completed"
  | "failed"
  | "canceled";

/** Mission-level error codes — closed set of THREE in contract.ts:183.
 *  `awaiting_user_timeout` is NOT in this closed set; it is a NODE-level
 *  open string set by the human-gate sweep on the record's `error` field
 *  when an `awaiting_user` mission's 7-day TTL expires. The pane keys off
 *  `record.error?.code === "awaiting_user_timeout"` to render the special
 *  "timed out waiting for your input" copy (handoff Part D, §6.4). */
export type MissionErrorCode = "plan_invalid" | "node_failed" | "mission_canceled";

/** Open-ended HTTP-layer error string the router emits alongside the
 *  three MissionErrorCode values. Keep this open — new codes show up as
 *  the router evolves. Callers should branch on known values + fall back
 *  to generic copy for unknown. */
export type MissionApiErrorCode =
  | MissionErrorCode
  // Resume / open command errors
  | "invalid_json"
  | "schema_validation_failed"
  | "payload_too_large"
  // Lookup / authz
  | "not_found"
  | "forbidden"
  | "unauthorized"
  // Result poll discriminator
  | "pending"
  // Human-gate sweep terminal cause (set on record.error.code by
  // mission-service/src/expiry-sweep)
  | "awaiting_user_timeout"
  // Open / catch-all
  | "internal"
  | "bad_gateway";

export interface MissionScope {
  orgId: string;
  clientId?: string;
  matterId?: string;
  caseId?: string;
}

export interface NodeError {
  /** Mission-level error code OR an open string from the leaf taxonomy
   *  (`awaiting_user_timeout` etc.). Treat as `string`; branch on known
   *  literals. */
  code: string;
  message: string;
}

export type NodeStatus =
  | "pending"
  | "dispatched"
  | "completed"
  | "failed"
  | "awaiting_user";

export interface ResultRef {
  type: string;
  ref: string;
  digest: string;
}

export interface MissionNodeState {
  nodeId: string;
  harnessId: string;
  taskId?: string;
  status: NodeStatus;
  resultRef?: ResultRef;
  output?: unknown;
  error?: NodeError;
  /** Lawyer-facing step label (mission-service contract.ts:123 —
   *  forwarded from `DagNode.summary` at dispatch). Render THIS as
   *  the per-step row heading in the detail dialog; fall back to
   *  the manifest label / `harnessId` only when absent (pre-ledger
   *  records). e.g. "Extract parties from the petition PDF". */
  summary?: string;
  // ── Ledger enrichment (mission-service contract.ts:128-135) ───────────
  /** Stamped on dispatch. */
  startedAt?: number;
  /** Stamped on terminal (completed / failed / awaiting_user resolve). */
  completedAt?: number;
  /** Convenience: completedAt - startedAt; mission-service computes it. */
  durationMs?: number;
  /** Rolled up from the leaf's task_usage on terminal. */
  costUsd?: number;
  tokensIn?: number;
  tokensOut?: number;
  /** Latest in-flight progress frame from the leaf (mirrors
   *  HarnTaskSummary.progress). Updates as the leaf reports. */
  progress?: { step: string; pct: number; message?: string };
}

/** Closed set of lifecycle event types in `MissionRecord.recentEvents`
 *  — contract.ts:140-148 MISSION_EVENT_TYPES. */
export type MissionEventType =
  | "state_changed"
  | "node_dispatched"
  | "node_completed"
  | "node_failed"
  | "node_awaiting_user"
  /** P7.3 — a node parked on a correlated bus reply (kind `handback`).
   *  A SIBLING of `node_awaiting_user`, never a reuse: mission-service's
   *  own contract docblock (contract.ts) instructs this dialog that it
   *  "must not narrate an agent park as a lawyer-facing ask". The label is
   *  therefore NEUTRAL and the counterparty rides in the event detail
   *  (`outcome.to`, mission-coordinator.ts:1368) — which matters because a
   *  handback park CAN be concierge-addressed, i.e. a human really does owe
   *  the reply (the ruled discriminator is the COUNTERPARTY, not the park
   *  kind). */
  | "node_awaiting_handback"
  | "wake_sent"
  /** The §Q4 wake POST exhausted its bounded retries UNDELIVERED. Was
   *  absent from this mirror, so it rendered as the raw enum on a neutral
   *  dot — a lost wake looked like routine chatter. */
  | "wake_failed"
  | "mission_canceled";

/** One entry in `MissionRecord.recentEvents` — the bounded lifecycle
 *  ring (≤20) used by the detail-dialog activity timeline. */
export interface MissionEvent {
  at: number;
  type: MissionEventType;
  nodeId?: string;
  detail?: string;
}

export interface MissionAwaitingUser {
  nodeId: string;
  /** Opaque to the SPA — each entry is leaf-defined `{question_id, ...}`. */
  questions: unknown[];
  /** Mission-service's own resume token (chat-mediated `lx_mission_resume`
   *  is the v1 caller; the pane does NOT POST this directly). */
  missionResumeToken: string;
  /** Token the underlying harness leaf is waiting on (chained from
   *  harn-service). Not used by the SPA in v1. */
  leafResumeToken: string;
  /** Unix ms — 7-day TTL window per `MISSION_AWAITING_USER_TTL_MS`. */
  expiresAt: number;
}

export interface MissionResult {
  artifacts: Array<{ key: string; type: string; ref: string; digest: string }>;
}

/** Computed (non-persisted) FIFO position for `state: "queued"` records
 *  (gating plan §D.2; router.ts:111 inlines this on `GET /missions/:id`). */
export interface MissionQueueInfo {
  position: number;
  reason: "waiting_for_case_slot" | "waiting_for_user_slot";
}

/** The summary shape `GET /missions` returns — NOT the full record.
 *  Drill-in (`GET /missions/:id`) returns `MissionRecord`. */
export interface MissionSummary {
  missionId: string;
  state: MissionState;
  goal: string;
  scope: MissionScope;
  openedAt: number;
  completedAt?: number;
}

/** Mirror of mission-service `DagNodeSchema` — the per-step plan node.
 *  v1.5: the spawner (lx_delegate / planner) supplies `summary` per
 *  node so the SPA can show a lawyer-facing one-liner per step (e.g.
 *  "Extract parties from the petition PDF") instead of the raw
 *  harnessId slug. */
export interface DagNode {
  id: string;
  harnessId: string;
  input?: unknown;
  dependsOn?: string[];
  summary?: string;
}

/** Mirror of mission-service `MissionPlanSchema`. */
export interface MissionPlan {
  nodes: DagNode[];
}

/**
 * The record's launched nodes, as a plain object that is always safe to
 * `Object.values`.
 *
 * Exists because `nodes` is optional on the wire while three call sites read it
 * as if it were not — and the cost of being wrong was the whole case view going
 * blank, not a missing row. Returns `{}` for an absent or non-object value.
 *
 * ⚠ `{}` here means **"no launched nodes"**, which is the honest reading: a
 * queued mission has none yet. It is NOT standing in for "the fetch failed" —
 * a failed fetch never reaches this function, because a rejected request leaves
 * the slot's previous record in place rather than writing a nodeless one.
 */
export function missionNodes(
  record: Pick<MissionRecord, "nodes"> | undefined | null,
): Record<string, MissionNodeState> {
  const nodes = record?.nodes;
  if (!nodes || typeof nodes !== "object") return {};
  return nodes;
}

/** Full record — `GET /missions/:id` payload, also the `record.nodes` is
 *  a Record (not Array) keyed by nodeId (contract.ts:163). */
export interface MissionRecord {
  missionId: string;
  sessionKey: string;
  goal: string;
  scope: MissionScope;
  state: MissionState;
  /** The DAG plan (set after `planning` completes). Per-node `summary`
   *  is what the detail dialog shows as the step's lawyer-facing
   *  caption — distinct from `nodes[id].progress.message` which is
   *  the live in-flight progress. */
  plan?: MissionPlan;
  /**
   * Map keyed by nodeId.
   *
   * ⚠ **OPTIONAL, and it was not always declared so** — this field was typed as
   * required, and its own docblock told callers to `Object.values(record.nodes)`
   * directly. On 2026-08-01 a record reached the store without it and
   * `Object.values(undefined)` threw "Cannot convert undefined or null to
   * object" *inside `<TeamOfAgentsPane>`*, which the route-level boundary turned
   * into a blank case view — unrecoverable by "Try again" or by reloading.
   *
   * A mission legitimately has no launched nodes (`state: "queued"` is the
   * obvious one — it is waiting on the admission gate and has not planned yet),
   * so absence is a REAL state and not only a malformed payload. Read it through
   * `missionNodes(record)` rather than reaching in; the required type was a
   * guarantee the SPA asserted and the wire never made.
   */
  nodes?: Record<string, MissionNodeState>;
  result?: MissionResult;
  /** `error.code === "awaiting_user_timeout"` is the human-gate-sweep
   *  cause the pane renders specially (handoff Part D). */
  error?: NodeError;
  awaitingUser?: MissionAwaitingUser;
  /** Present only when `state === "queued"` (router.ts:111). */
  queueInfo?: MissionQueueInfo;
  openedAt: number;
  startedAt?: number;
  completedAt?: number;
  /** Persisted on the record so a queued mission admitted later
   *  (or recovered after a restart) can still plan (gating §C.2). */
  plannerHarnessId?: string;
  // ── Mission-level ledger enrichment (contract.ts:199-202) ─────────────
  /** Cost + token roll-up across all leaves (summed at terminal). */
  totals?: { costUsd: number; tokensIn: number; tokensOut: number };
  /** Last ≤20 lifecycle events (FIFO ring) — detail-dialog activity timeline. */
  recentEvents?: MissionEvent[];
}

/** Terminal mission states (contract.ts:41) — mirror of the runtime's
 *  TERMINAL_MISSION_STATES tuple. Used by callers that need to gate
 *  "is this mission still mutable?" (cancel / await-user / etc.). */
export const TERMINAL_MISSION_STATES: readonly MissionState[] = [
  "completed",
  "failed",
  "canceled",
];

export interface MissionEnvelope {
  goal: string;
  scope: MissionScope;
  /** Opening turn's session key — round-trips to the §Q4 concierge wake. */
  sessionKey: string;
  inputs?: Record<string, unknown>;
  plan?: unknown; // MissionPlan — opaque to the SPA in v1
  plannerHarnessId?: string;
  synthesizerHarnessId?: string;
}

/** `POST /mission/open` response — 202 status, just the new mission id +
 *  initial state (queued | submitted). Callers fetch the full record via
 *  `getMission(id)` afterwards. */
export interface MissionOpenResponse {
  missionId: string;
  state: MissionState;
}

/** `GET /missions/:id/result` is polymorphic per router.ts:120-133.
 *  `pending` (409) is thrown as `MissionApiError(status:409, code:"pending")`
 *  — callers use `isMissionPending(err)` to keep polling. */
export type MissionResultResponse =
  | { ok: true; result: MissionResult }
  // `error` is `.optional()` on the server schema (contract.ts
  // NodeErrorSchema) — types must match. Consumers MUST null-check
  // `error?.code` before reading (in practice the human-gate sweep
  // always populates it, but the generic `node_failed` path is
  // schema-optional).
  | { ok: false; error?: NodeError }
  | {
      ok: false;
      state: "awaiting_user";
      questions: unknown[];
      // mission-service emits snake_case on this endpoint only
      resume_token: string;
      expires_at: number;
    };

export interface MissionTasksResponse {
  tasks: Array<{ nodeId: string; taskId?: string; status: NodeStatus }>;
}

// ─── Error class (mirror LxOpenclawConfigError shape) ─────────────────

/** Typed error for `/api/mission/*` calls. Carries the canonical
 *  `code` string from §B.5 of the architecture (closed `MissionErrorCode`
 *  + the open HTTP-layer codes the router emits) AND the HTTP status so
 *  the UI can branch (e.g. 409 + code `pending` continues a result-poll;
 *  401 logs out; 403 hides the row; 404 is benign on cancel of a queued
 *  mission). `details` is the structured per-issue payload mission-
 *  service's router emits on `schema_validation_failed` (Zod's
 *  `err.issues`) — preserve it so callers can surface field-level
 *  validation feedback. Mutable to match cousin `LxOpenclawConfigError`
 *  shape. */
export class MissionApiError extends Error {
  status: number;
  code?: string;
  missionId?: string;
  details?: unknown;
  constructor(
    message: string,
    status: number,
    code?: string,
    missionId?: string,
    details?: unknown,
  ) {
    super(message);
    this.name = "MissionApiError";
    this.status = status;
    this.code = code;
    this.missionId = missionId;
    this.details = details;
  }
}

/** Convenience for callers polling `getMissionResult`. Router.ts:133
 *  returns 409 `{error:{code:"pending"}}` while submitted|planning|
 *  running|synthesizing — those are NOT terminal; keep polling.
 *
 *  Note: 409 is ALSO emitted by the router for `mission_canceled`
 *  (MissionError → `missionErrorStatus`, router.ts:37); the
 *  `code === "pending"` check is load-bearing — status alone is NOT
 *  enough to discriminate the keep-polling sentinel from a terminal
 *  cancel. */
export const isMissionPending = (e: unknown): boolean =>
  e instanceof MissionApiError && e.status === 409 && e.code === "pending";

/** True when a `failed` MissionRecord's terminal cause is the human-gate
 *  7-day sweep (handoff Part D copy: "timed out waiting for your input"). */
export const isAwaitingUserTimeout = (r: MissionRecord): boolean =>
  r.state === "failed" && r.error?.code === "awaiting_user_timeout";

// ─── Internal helpers ─────────────────────────────────────────────────

const ROOT = "/api/mission";
const DEFAULT_TIMEOUT_MS = 8_000;

async function authedHeaders(): Promise<Record<string, string>> {
  const token = await getToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    traceparent: newTraceparent(),
  };
}

interface ErrorEnvelope {
  // Router emits the bare-string form on most paths
  // (`{error: "not_found"}`); MissionError throws produce the object form
  // (`{error: "plan_invalid", message: "…"}`); the global `app.onError`
  // produces `{error: "internal", message: "…"}`. Object-with-code form
  // is included for forward-compat with future handlers.
  error?: string | { code?: string; message?: string };
  message?: string;
  /** Structured per-issue payload — `Zod.ZodIssue[]` on
   *  `schema_validation_failed` (router.ts:76). Preserved so the pane
   *  can surface field-level "field X is missing" feedback instead of
   *  the synthetic top-level message. */
  details?: unknown;
}

// Cousin `lx-openclaw-config.ts` uses a per-endpoint `fetch +
// throwFromResponse` pattern. We unify both halves (fetch + error
// normalisation) into a single `request<T>` helper here because
// mission-service returns RAW JSON (no `{ok:true, data:...}` envelope
// wrap — see file-header §Auth+envelope rules), so the per-call shape
// in cousins (`body.data` unwrap) doesn't apply, and the timeout +
// traceparent + auth boilerplate is identical for every endpoint.
async function request<T>(
  method: "GET" | "POST" | "DELETE",
  path: string,
  opts?: {
    body?: unknown;
    timeoutMs?: number;
    missionIdForError?: string;
  },
): Promise<T> {
  const ctl = new AbortController();
  const timer = setTimeout(
    () => ctl.abort(),
    opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS,
  );
  try {
    const res = await fetch(`${ROOT}${path}`, {
      method,
      headers: await authedHeaders(),
      body: opts?.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: ctl.signal,
    });
    const raw = (await res.json().catch(() => ({}))) as ErrorEnvelope;
    if (!res.ok) {
      let code: string | undefined;
      let message: string | undefined;
      if (typeof raw.error === "string") {
        code = raw.error;
        message = raw.message;
      } else if (raw.error && typeof raw.error === "object") {
        code = raw.error.code;
        message = raw.error.message ?? raw.message;
      } else {
        message = raw.message;
      }
      throw new MissionApiError(
        message ?? `mission-service ${method} ${path} returned ${res.status}`,
        res.status,
        code,
        opts?.missionIdForError,
        raw.details,
      );
    }
    return raw as T;
  } finally {
    clearTimeout(timer);
  }
}

// ─── Public API ───────────────────────────────────────────────────────

/** `POST /api/mission/mission/open` — doubled `/mission/` prefix is the
 *  COMMAND route form (see file header). 202 on success. v1: the pane does
 *  NOT open missions directly; the concierge does via `lx_delegate`. This
 *  is exported for future direct affordances + tests. */
export const openMission = (env: MissionEnvelope) =>
  request<MissionOpenResponse>("POST", "/mission/open", { body: env });

export interface ListMissionsFilters {
  state?: MissionState;
  caseId?: string;
  orgId?: string;
}

/** `GET /api/mission/missions` — collection route. Returns SUMMARY rows
 *  (no nodes / no result), chronological by ULID missionId so
 *  `?state=queued` is the FIFO admission queue (gating plan §D.1). */
export const listMissions = (
  f: ListMissionsFilters = {},
): Promise<{ missions: MissionSummary[] }> => {
  const qs = new URLSearchParams();
  if (f.state) qs.set("state", f.state);
  if (f.caseId) qs.set("caseId", f.caseId);
  if (f.orgId) qs.set("orgId", f.orgId);
  const q = qs.toString();
  return request<{ missions: MissionSummary[] }>(
    "GET",
    `/missions${q ? `?${q}` : ""}`,
  );
};

/** `GET /api/mission/missions/:id` — full record. `queueInfo` is inlined
 *  when `state === "queued"` (router.ts:111). */
export const getMission = (missionId: string) =>
  request<MissionRecord>(
    "GET",
    `/missions/${encodeURIComponent(missionId)}`,
    { missionIdForError: missionId },
  );

/**
 * The `GET /missions/:id/awaiting` discovery view (mission-service-guide
 * §7-bis). Serves what · why · thread · briefing · since · expiresAt with ZERO
 * bus calls — the ask is mirrored onto the record at park time, so it answers
 * with both bus switches OFF. `409 not_awaiting` when the mission is not parked.
 *
 * ⚠ `kind` splits the TWO park kinds that SHARE the `awaiting_user` state, and
 * the distinction is load-bearing for the SPA, not cosmetic:
 *   - `"user"`     — the HUMAN gate. The lawyer is who it waits for, and the
 *                    v1 answer path exists (chat-mediated `lx_mission_resume`).
 *   - `"handback"` — the AGENT park. Per §7-bis it has **NO REST door**: the
 *                    in-process reply bridge is the only caller. A human cannot
 *                    answer it, so offering them a send would be a lie.
 * ⚠ Read `kind` as an OPEN vocabulary: an unrecognised value must degrade to
 * the SAFE side (not answerable), never be assumed answerable.
 */
export interface MissionAwaitingView {
  ok: true;
  /** `"user" | "handback"` today — OPEN, see above. */
  kind: string;
  /** Unflattened briefing parts, as parked. The questions live inside. */
  briefing?: { parts?: unknown[] };
  what?: string;
  why?: string;
  thread?: string;
  since?: number;
  expiresAt?: number;
}

/** `GET /api/mission/missions/:id/awaiting` — the §7-bis discovery pull.
 *  Throws `409 not_awaiting` when the mission is no longer parked, which is
 *  exactly the signal that a rehydrated card must NOT be shown. */
export const getMissionAwaiting = (missionId: string) =>
  request<MissionAwaitingView>(
    "GET",
    `/missions/${encodeURIComponent(missionId)}/awaiting`,
    { missionIdForError: missionId },
  );

/** `GET /api/mission/missions/:id/result` — polymorphic body (see
 *  `MissionResultResponse`). 409 + `code:"pending"` is the "still
 *  running" sentinel; use `isMissionPending(err)` to keep polling. */
export const getMissionResult = (missionId: string) =>
  request<MissionResultResponse>(
    "GET",
    `/missions/${encodeURIComponent(missionId)}/result`,
    { missionIdForError: missionId },
  );

/** `GET /api/mission/missions/:id/tasks` — flat node→task projection. */
export const getMissionTasks = (missionId: string) =>
  request<MissionTasksResponse>(
    "GET",
    `/missions/${encodeURIComponent(missionId)}/tasks`,
    { missionIdForError: missionId },
  );

/** `POST /api/mission/mission/:id/resume` — doubled `/mission/` prefix is
 *  the COMMAND route form. v1: the pane does NOT call this — the
 *  concierge does via `lx_mission_resume` (chat-mediated). Exported for
 *  the deferred direct-answer affordance + tests.
 *
 *  DO NOT WIRE TO UI WITHOUT APPROVAL — handoff Part D explicitly defers
 *  the direct-answer affordance to v1.1. The `MissionsPane`'s
 *  awaiting-user banner sends the lawyer to chat, not this endpoint. */
export const resumeMission = (
  missionId: string,
  body: { token: string; answers?: unknown[] },
) =>
  request<{ state: MissionState }>(
    "POST",
    `/mission/${encodeURIComponent(missionId)}/resume`,
    { body, missionIdForError: missionId },
  );

/** `DELETE /api/mission/missions/:id` — works on `queued` too (admission
 *  gate releases the slot, gating plan §F.2). Returns `{canceled: bool}`. */
export const cancelMission = (missionId: string) =>
  request<{ canceled: boolean }>(
    "DELETE",
    `/missions/${encodeURIComponent(missionId)}`,
    { missionIdForError: missionId },
  );

/** `GET /api/mission/health` — `{ok:true, counts: {...}}`. Cheap probe;
 *  used by the live-smoke gate, not by the pane itself. */
export const missionHealth = () =>
  request<{ ok: true; counts: Record<string, number> }>("GET", "/health");

// NOTE: §5.6 milestone-frame vocabulary (`mission_admitted`,
// `node_dispatched`, `node_completed`, …) is intentionally NOT modelled
// here — those frames travel over WS, and browsers get 501 on upgrade.
// Phase (c) pane code derives the same narrative from polled state
// transitions; revisit if/when SSE-relay ships.
